import React from 'react'
import { TFunction } from 'i18next'
import moment from 'moment'
import { Empty } from 'src/components/atoms'
import { R12Data, R12Header } from 'src/store/ducks/reports/types'
import { Title } from 'src/components/Ant'
import { List } from './block-list'
import { Spacer } from './styles'
import SectionHeader from './section-header'
import { ControlReportRefProps, Page, PageItem } from '../types'
import TitleReport from '../../title-report'

const mesures = {
  LIST_HEADER: 38,
  LIST_TITLE: 38,
  MINIMUM_BLOCK_JUSTIFICATION: 117.76,
  MINIMUM_BLOCK: 97.76,
  PAGE_HEIGHT: 1020,
  REGISTER_SINGLE: 21,
  REGISTER_WITH_JUSTIFICATION: 41,
  REPORT_HEADER: 96,
  REPORT_TITLE: 55.5,
  SPACER: 16
}

function notNull(value: string | number) {
  return !value ? '' : value
}

export function getCvsHeader(header: R12Header[], t: TFunction) {
  const { end_date, participant_name, service, start_date } = header?.[0] || {}
  return `${t('web.report.12.header.service')},${notNull(service)}\n${t(
    'web.report.12.header.participant_name'
  )},${notNull(participant_name)}\n${t('web.report.12.header.start_date')},${
    moment(moment.unix(start_date).utc().format('YYYYMMDD')).format(
      'DD/MM/YYYY'
    ) || '-'
  }\n${t('web.report.12.header.end_date')},${
    moment(moment.unix(end_date).utc().format('YYYYMMDD')).format(
      'DD/MM/YYYY'
    ) || '-'
  }\n\n${t('web.resources.common.name.title')},${t(
    'web.report.12.data.date'
  )},${t('web.report.12.data.request-number')},${t(
    'web.report.12.data.quantity'
  )},${t('web.report.12.data.total')}\n`
}

export function generateCvsReduce(acc: string, cur: R12Data) {
  return acc.concat(
    `${cur.fullname},${
      moment(moment.unix(cur.datetrunc).utc().format('YYYYMMDD')).format(
        'DD/MM/YYYY'
      ) || '-'
    },${cur.request_number},${cur.quantity},${cur.total.toFixed(2)}\n`
  )
}

export function thereIsSpaceForMore(
  ref: React.MutableRefObject<ControlReportRefProps>,
  data: R12Data[]
): boolean {
  if (ref.current.reportIndex >= data.length) {
    return false
  }

  const nextFieldHeight = data[ref.current.reportIndex].justification
    ? mesures.MINIMUM_BLOCK_JUSTIFICATION
    : mesures.MINIMUM_BLOCK

  return nextFieldHeight + mesures.SPACER <= ref.current.availableSpace
}

export function getRangedBlocks(
  ref: React.MutableRefObject<ControlReportRefProps>,
  data: R12Data[]
): number {
  let indexStart = ref.current.reportIndex
  const activeUserName = data[ref.current.reportIndex].fullname

  do {
    const filedHeight = data[indexStart].justification
      ? mesures.REGISTER_WITH_JUSTIFICATION
      : mesures.REGISTER_SINGLE

    if (
      activeUserName !== data[indexStart].fullname ||
      ref.current.availableSpace - filedHeight < 0
    ) {
      return indexStart
    }

    ref.current.availableSpace -= filedHeight
    indexStart++
  } while (indexStart < data.length)

  return indexStart
}

export function generateReportPages(
  ref: React.MutableRefObject<ControlReportRefProps>,
  data: R12Data[],
  header: R12Header[]
): Page[] {
  const pages: Page[] = []

  if (!data.length) {
    return [
      {
        index: 0,
        content: [
          {
            index: 1,
            content: (
              <>
                <TitleReport title="web.report.12.title" />
                <SectionHeader data={header} />
                <Empty />
              </>
            )
          }
        ]
      }
    ]
  }

  do {
    const page: PageItem[] = []
    ref.current.availableSpace = mesures.PAGE_HEIGHT

    page.push({
      index: ref.current.nextIndex++,
      content: <TitleReport title="web.report.12.title" />
    })
    ref.current.availableSpace -= mesures.REPORT_TITLE

    if (ref.current.reportIndex === 0) {
      page.push({
        index: ref.current.nextIndex++,
        content: <SectionHeader data={header} />
      })
      ref.current.availableSpace -= mesures.REPORT_HEADER
    }

    do {
      page.push({
        index: ref.current.nextIndex++,
        content: (
          <Title level={4}>{data[ref.current.reportIndex].fullname}</Title>
        )
      })
      ref.current.availableSpace -= mesures.LIST_TITLE
      ref.current.availableSpace -= mesures.LIST_HEADER

      const rangedBlocks = getRangedBlocks(ref, data)

      page.push({
        index: ref.current.nextIndex++,
        content: (
          <List items={data.slice(ref.current.reportIndex, rangedBlocks)} />
        )
      })

      ref.current.reportIndex = rangedBlocks

      if (thereIsSpaceForMore(ref, data)) {
        page.push({
          index: ref.current.nextIndex++,
          content: <Spacer />
        })
        ref.current.availableSpace -= mesures.SPACER
      } else {
        break
      }
    } while (ref.current.reportIndex < data.length)
    pages.push({
      index: pages.length,
      content: page
    })
  } while (ref.current.reportIndex < data.length)

  return pages
}

export function handleDownloadCsv(csvString: BlobPart, t: TFunction) {
  const blob = new Blob([csvString], { type: 'text/csv' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `${t('web.report.12.title')}.csv`
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}
